import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { query } from "@/services/services";
import { PageHeader } from "@/components/common/PageHeader";
import { Editor } from "@/components/drafting/editor/EditorShell";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { Panel } from "@/components/common/Panel";
import { formatDate, fromNow } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { QuickAddDialogs } from "@/components/shell/QuickAddDialogs";
import { MatterChip } from "@/components/common/MatterSelector";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  PenLine, Bold, Italic, Underline, List, Quote, GitCompare, History, Eye, Download,
  ChevronDown, Sparkles, Check, Plus,
} from "lucide-react";


export default function Drafting() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { db } = useApp();
  const ctxMatterId = new URLSearchParams(window.location.search).get("matterId");
  const [newDraft, setNewDraft] = useState(false);
  const ctxMatter = ctxMatterId ? query.matter(db, ctxMatterId) : null;
  const drafts = ctxMatterId ? db.drafts.filter((d) => d.matterId === ctxMatterId) : db.drafts;

  if (!id) {
    return (
      <div>
        <PageHeader title="Drafting Studio" subtitle={ctxMatter ? `Working in matter context — drafts for ${ctxMatter.title}.` : "Draft, review and manage legal documents with intelligence at your side."}>
          {ctxMatter && <MatterChip matterId={ctxMatterId} allowChange={false} />}
          {ctxMatter && <Button onClick={() => setNewDraft(true)} data-testid="drafting-new-for-matter">+ New Draft for this Matter</Button>}
          {newDraft && <QuickAddDialogs type="draft" context={{ matterId: ctxMatterId }} onClose={() => setNewDraft(false)} />}
          {!ctxMatter && <Button onClick={() => db.drafts[0] && navigate(`/drafting/${db.drafts[0].id}`)}>Open Studio</Button>}
        </PageHeader>
        {drafts.length === 0 ? <EmptyState icon={PenLine} title="No drafts yet" message={ctxMatter ? "Create the first draft for this matter — it will carry matter context." : "Create a draft from any matter to begin."} /> :
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {drafts.map((d) => {
              const m = query.matter(db, d.matterId);
              return (
                <button key={d.id} onClick={() => navigate(`/drafting/${d.id}`)} className="rounded-xl border border-line bg-card p-4 text-left hover:border-copper/40" data-testid={`draft-card-${d.id}`}>
                  <div className="flex items-start justify-between"><PenLine className="h-6 w-6 text-copper" /><StatusBadge status={d.status} /></div>
                  <div className="mt-3 font-medium text-ink">{d.title}</div>
                  <div className="text-xs text-ink-2">{d.type} · v{d.version}</div>
                  <div className="mt-2 text-xs text-ink-muted">{m?.caseNumber} · edited {fromNow(d.updatedAt)}</div>
                </button>
              );
            })}
          </div>}
      </div>
    );
  }

  return <Editor draftId={id} />;
}

